/**
 * The turns the SERVER knows about, for this editor.
 *
 * The history picker used to read IndexedDB alone, which holds only what THIS
 * browser saw. Two things were therefore invisible: a turn running in another
 * tab, and — the one that loses work — a turn whose browser was closed before
 * the answer arrived. The run outlives the browser and writes itself down when
 * it ends, so the server can still answer both.
 *
 * Never fatal: a sidecar that is down leaves the picker showing local history,
 * which is what it showed before this existed.
 */

const ENDPOINT = '/api/builder/chat/runs'
const READ_ENDPOINT = '/api/builder/chat/read'

/**
 * Les conversations dont CE chargement de page a vu le tour tourner.
 *
 * Un tour d'arrière-plan qui se termine cessait d'être « en cours » et n'était
 * plus annoncé qu'en bas du panneau, parmi les réponses « arrivées pendant votre
 * absence » — alors que l'éditeur, lui, ne s'était pas absenté : il regardait la
 * ligne tourner. Ce registre est ce qui distingue les deux situations, et il ne
 * peut pas venir du serveur : la question n'est pas « qui a lu ? » mais « cette
 * page-ci l'a-t-elle vu tourner ? ».
 *
 * Au niveau du MODULE, et volontairement NI `sessionStorage` NI un état React :
 * il doit survivre au démontage du panneau (changer d'écran dans l'application
 * ne recharge rien) et mourir avec le document. Recharger la page, c'est
 * exactement ce qui doit renvoyer ces réponses au bandeau du bas.
 */
const seenLive = new Set()

/** Ce que la page a vu tourner. Alimenté par chaque sondage (cf. fetchChatRuns). */
export const seenLiveHere = (conversationId) => seenLive.has(conversationId)

/** Oublier tout : un rechargement de page, en somme. Pour les tests. */
export const forgetLiveRuns = () => seenLive.clear()

/**
 * @returns {Promise<Array<{conversationId, runId, status, title, startedAt,
 *   endedAt, from, queuePosition, delivered, stored}>>}
 *   `status` is `running` | `queued` | `done` | `error` | `aborted`.
 *   `delivered: false` marks an answer produced while the editor was away and
 *   not yet read. `from` is the cursor to hand back to /attach to replay it.
 */
export async function fetchChatRuns(signal) {
  try {
    const res = await fetch(ENDPOINT, { credentials: 'include', cache: 'no-store', signal })
    if (!res.ok) return []
    const body = await res.json()
    const runs = Array.isArray(body?.runs) ? body.runs : []
    // Noté ICI, la porte unique par laquelle les tours entrent : un appelant qui
    // oublierait de le faire perdrait la mémoire de ce que la page a vu tourner.
    for (const r of runs) if (isLive(r)) seenLive.add(r.conversationId)
    return runs
  }
  catch { return [] }
}

/**
 * Tell the server these answers have been seen.
 *
 * Lowers the stored `delivered` flag, which is what the unread badge and the
 * "arrived while you were away" banner read. Without it the only way to silence
 * an announcement was to REOPEN the conversation — a detour for an editor who
 * already knows what it is about.
 *
 * Never fatal: a failure leaves the answers unread, and the banner comes back
 * on the next poll rather than lying about it.
 *
 * @returns {Promise<boolean>} whether the server took it.
 */
export async function markRunsRead(conversationIds) {
  const ids = (conversationIds ?? []).filter(Boolean)
  if (ids.length === 0) return false
  try {
    const res = await fetch(READ_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationIds: ids }),
    })
    return res.ok
  }
  catch { return false }
}

/**
 * L'adresse d'où part une conversation est-elle un chemin interne, donc sûre à
 * rendre comme un lien ?
 *
 * Elle est POSTÉE par le navigateur avant d'être réaffichée : un `javascript:`
 * déposé par une requête forgée deviendrait un lien exécutable. Seul un chemin
 * absolu de ce site en est un — ni `//hôte` (relatif au protocole, donc
 * externe), ni un schéma. Le reste s'affiche en texte, sans lien.
 */
export const isSafeOrigin = (path) => typeof path === 'string' && /^\/(?!\/)/.test(path)

/**
 * Paramètre d'URL qui demande à l'assistant de s'ouvrir SUR une conversation.
 * Lu au montage du panneau (cf. BuilderChatPanel).
 */
export const CHAT_PARAM = 'chat'

/**
 * Où mène le lien d'une conversation : la page d'où elle est partie, ET de quoi
 * y rouvrir l'assistant dessus.
 *
 * Sans le paramètre, le lien ramènerait sur la bonne page en ayant perdu ce
 * qu'on y cherchait — l'éditeur devrait rouvrir l'historique et retrouver la
 * ligne à la main. Rend `null` quand l'adresse n'est pas un chemin interne :
 * l'appelant affiche alors du texte, pas un lien (cf. isSafeOrigin).
 */
export function originHref(origin, conversationId) {
  if (!isSafeOrigin(origin)) return null
  // Le fragment est jeté : il ne désigne pas une page, et il devrait de toute
  // façon repasser APRÈS la requête.
  const clean = origin.split('#')[0]
  const cut = clean.indexOf('?')
  const path = cut === -1 ? clean : clean.slice(0, cut)
  const params = new URLSearchParams(cut === -1 ? '' : clean.slice(cut + 1))
  if (conversationId) params.set(CHAT_PARAM, conversationId)
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

/** Is this turn still going (or waiting for its slot)? */
export const isLive = (run) => run?.status === 'running' || run?.status === 'queued'

/**
 * Merge the server's view into the local history, without losing either.
 *
 * A conversation the browser knows keeps its stored transcript and gains the
 * server's live status. One it has never seen — started elsewhere, or answered
 * after this browser was closed — becomes a row of its own, with the question as
 * its title so it can be recognised.
 */
export function mergeRuns(entries, runs) {
  const byId = new Map(runs.map(r => [r.conversationId, r]))
  const merged = entries.map((entry) => {
    const run = byId.get(entry.id)
    if (!run) return entry
    // `origin` : la trace locale d'abord, celle du serveur en repli. Une
    // conversation démarrée dans un autre navigateur n'a que la seconde, et une
    // conversation antérieure à cette version n'a ni l'une ni l'autre.
    return { ...entry, origin: entry.origin || run.origin || '', run }
  })

  const known = new Set(entries.map(e => e.id))
  const unknown = runs
    .filter(r => !known.has(r.conversationId))
    .map(r => ({
      id: r.conversationId,
      title: r.title || 'Conversation',
      origin: r.origin || '',
      sessionId: null,
      messages: [],
      updatedAt: r.endedAt ?? r.startedAt ?? 0,
      run: r,
    }))

  return [...merged, ...unknown].sort((a, b) => {
    // Anything still going floats to the top: it is the only row with something
    // happening behind it.
    const liveDiff = Number(isLive(b.run)) - Number(isLive(a.run))
    if (liveDiff !== 0) return liveDiff
    return (b.updatedAt ?? 0) - (a.updatedAt ?? 0)
  })
}

/**
 * Les tours qui tournent AILLEURS que sous les yeux de l'éditeur.
 *
 * `attachedId` est la conversation dont le flux est BRANCHÉ, pas celle qui est
 * affichée : panneau fermé, on détache tout en gardant sa conversation à
 * l'écran, et son tour compte alors comme n'importe quel autre. L'appelant passe
 * donc `isStreaming ? conversationId : null`.
 *
 * `isLive` couvre `running` ET `queued` : un tour en file produira une réponse,
 * il compte — mais l'appelant doit le DIRE séparément, parce qu'annoncer « en
 * cours » quelque chose qui n'a pas commencé est un mensonge que l'éditeur
 * vérifie tout seul en ouvrant l'historique.
 */
export const backgroundRuns = (runs, attachedId) =>
  (runs ?? []).filter(run => isLive(run) && run.conversationId !== attachedId)

/**
 * Les tours qui viennent de PASSER de « en cours » à « terminé » entre deux
 * sondages.
 *
 * C'est la seule façon de le savoir : `/chat/runs` rend un ÉTAT, pas un flux
 * d'événements. Sans la comparaison on ne distinguerait pas une réponse qui
 * vient de finir d'une réponse qui attend depuis ce matin — et c'est exactement
 * la frontière entre ce signalement-ci et le bandeau « pendant votre absence ».
 *
 * Le premier sondage n'a pas de `prev` : rien n'est annoncé au montage. C'est
 * VOULU, et c'est ce qui rend la non-duplication structurelle plutôt
 * qu'heuristique — ce qui s'est terminé avant qu'on regarde appartient au
 * bandeau, pas à ce signalement.
 */
export function newlyFinished(prevRuns, nextRuns, attachedId) {
  const before = new Map((prevRuns ?? []).map(run => [run.conversationId, run]))
  return (nextRuns ?? []).filter((run) => {
    // On l'a regardée finir : ce n'est pas une nouvelle.
    if (run.conversationId === attachedId) return false
    if (isLive(run)) return false
    const was = before.get(run.conversationId)
    // Même run : une conversation qui relance un DEUXIÈME tour ne doit pas se
    // lire comme la fin du premier.
    return !!was && isLive(was) && was.runId === run.runId
  })
}

/**
 * Les tours TERMINÉS que cette page a vus tourner, et que personne n'a encore lus.
 *
 * Ils restent affichés LÀ OÙ ils tournaient — en tête du panneau, à côté des
 * tours vivants — au lieu de descendre parmi les réponses « arrivées pendant
 * votre absence » : l'éditeur n'était pas absent, il a regardé la ligne tourner,
 * et la voir sauter d'un bout à l'autre du panneau au moment où elle aboutit
 * fait perdre ce qu'on suivait. Un rechargement vide le registre, et elles
 * repartent alors dans le bandeau du bas — c'est là qu'est leur place quand
 * l'éditeur revient d'ailleurs.
 *
 * `delivered !== true` plutôt que `=== false` : un tour terminé que le registre
 * du sidecar tient encore rend `delivered: null` (il n'est lu que sur disque)
 * pendant une dizaine de minutes, et l'exiger à `false` laisserait un trou juste
 * après la fin du tour — l'instant même que cette liste doit couvrir.
 */
export const finishedHereRuns = (runs, attachedId) => (runs ?? []).filter(run => (
  !isLive(run)
  && run.conversationId !== attachedId
  && run.delivered !== true
  && seenLiveHere(run.conversationId)
))

/** Turns finished while the editor was away and still unread. */
export const unreadRuns = (runs) => runs.filter(r => r.stored && r.delivered === false)

/**
 * Celles de ces réponses que le bandeau du bas doit encore annoncer.
 *
 * Ce que cette page a vu tourner est déjà en haut (cf. finishedHereRuns) :
 * l'annoncer une seconde fois ferait lire deux réponses là où il n'y en a qu'une.
 */
export const unreadElsewhereRuns = (runs) => unreadRuns(runs).filter(r => !seenLiveHere(r.conversationId))
